<template>
    <v-container fluid>
        <v-row class="pb-0">
        <v-col lg=4 sm=12 md=12 cols=12>
            <v-row>
                <!-- Block 1: Stock Overview -->
                <v-col lg=12 sm=12 md=12 cols=12 class="pb-0" :class="{'px-0': $vuetify.breakpoint.mdAndDown}">
                    <v-card outlined elevation="2" class="card-style">
                    <v-card-title class="card-title d-flex justify-space-between align-center">
                        <p class="text-h5 text-truncate">{{ $t('signalDetail.overview.title') }}</p>
                        <v-btn icon :disabled="loadingSignalDetail || loadingFavoriteResult" :color="isFavoriteSignal ? 'red' : 'grey lighten-1'" @click="updateFavoriteStatus">
                            <v-icon>{{ isFavoriteSignal ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-skeleton-loader
                            v-if="loadingSignalDetail"
                            type="image"
                            max-height="200"
                        ></v-skeleton-loader>
                        <div v-else>
                            <p><strong>{{ $t('signalDetail.overview.company') }}:</strong> {{ signalDetail.stock.ticker }} | {{ signalDetail.stock.name }}</p>
                            <div class="pb-4">
                                <strong>{{ $t('signalDetail.overview.description') }}:</strong>
                                <span v-if="!signalDetail.stock.description || signalDetail.stock.description.length < descriptionPreviewMaxSize">
                                    {{ signalDetail.stock.description ? signalDetail.stock.description : $t('general.na') }}
                                </span>
                                <span v-else>
                                    {{ signalDetail.stock.description.substr(0, descriptionPreviewMaxSize) }}
                                    <span id="desc-dots">...&nbsp;</span>
                                    <span id="desc-more" style="display: none;">{{ signalDetail.stock.description.substr(descriptionPreviewMaxSize) }}&nbsp;</span>
                                    <a id="desc-link" href="javascript:undefined" @click="updateDescriptionStatus(false)">{{ $t('signalDetail.overview.showMore') }}</a>
                                </span>
                            </div>
                            <p><strong>{{ $t('signalDetail.overview.si') }}:</strong> {{ signalDetail.stock.sector }} / {{ signalDetail.stock.industry }}</p>
                            <p><strong>{{ $t('signalDetail.overview.marketCapitalization') }}:</strong> ${{ formatNumber(signalDetail.stock.marketCapitalization, false) }} </p>
                            <p><strong>{{ $t('signalDetail.overview.nextEarning') }}:</strong> {{ signalDetail.stock.nextEarningDate ? formatDate(signalDetail.stock.nextEarningDate) : $t('general.na') }}</p>
                        </div>
                    </v-card-text>
                    </v-card>
                </v-col>

                <!-- Block 2: Key Indicators -->
                <v-col lg=12 sm=12 md=12 cols=12 class="pb-0" :class="{'px-0': $vuetify.breakpoint.mdAndDown}">
                    <v-card outlined elevation="2" class="card-style">
                    <v-card-title class="card-title"><p class="text-h5 text-truncate">{{ $t('signalDetail.indicators.title') }}</p></v-card-title>
                    <v-card-text>
                        <v-skeleton-loader
                            v-if="loadingSignalDetail"
                            type="image"
                            class="height-450"
                        ></v-skeleton-loader>
                        <div v-else>
                            <div v-if="signalDetail.finished" class="same-line-flex">
                                <p class="ma-0"><strong>{{  $t('signalDetail.indicators.maximumProfit') }}:&nbsp;</strong></p>
                                <div class="text-center text-no-wrap">
                                    {{calculateGrowthPercentageFormatted(signalDetail.keyIndicators.peakPrice, signalDetail.keyIndicators.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                                </div>
                            </div>
                            <div v-else-if="signalDetail.triggered" class="same-line-flex">
                                <p class="ma-0"><strong>{{  $t('signalDetail.indicators.currentStatus') }}:&nbsp;</strong></p>
                                <div v-if="signalDetail.keyIndicators.currentPrice >= signalDetail.keyIndicators.signalPrice" class="text-center text-no-wrap">
                                    {{calculateGrowthPercentageFormatted(signalDetail.keyIndicators.currentPrice, signalDetail.keyIndicators.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                                </div>
                                <div v-else class="text-center text-no-wrap">
                                    {{calculateGrowthPercentageFormatted(signalDetail.keyIndicators.currentPrice, signalDetail.keyIndicators.signalPrice)}}% <v-icon color="error">mdi-arrow-bottom-right</v-icon>
                                </div>
                            </div>
                            <div v-else class="same-line-flex">
                                <p class="ma-0"><strong>{{  $t('signalDetail.indicators.pendingPrice') }}:&nbsp;</strong></p>
                                <div class="text-center text-no-wrap">
                                    {{calculateGrowthPercentageFormatted(signalDetail.keyIndicators.currentPrice, signalDetail.keyIndicators.signalPrice, true)}}%
                                </div>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <p><strong>{{ signalDetail.triggered ? $t('signalDetail.indicators.triggeredPrice') : $t('signalDetail.indicators.signalPrice') }}:</strong> $ {{ formatNumber(signalDetail.keyIndicators.signalPrice, false) }}</p>
                            <div class="same-line-flex">
                                <p class="ma-0 min-width-205"><strong>{{ signalDetail.triggered ? $t('signalDetail.indicators.triggeredVolume') : $t('signalDetail.indicators.signalVolume') }}:</strong> 
                                    {{ formatNumber(signalDetail.triggered ? signalDetail.keyIndicators.breakoutVolume : signalDetail.keyIndicators.signalVolume, false) }}
                                </p>
                                <v-progress-circular
                                    key="volume-completion"
                                    :value="signalDetail.triggered ? 
                                            calculateVolumeCompletion(signalDetail.keyIndicators.breakoutVolume, signalDetail.keyIndicators.signalVolume) : 
                                            calculateVolumeCompletion(signalDetail.keyIndicators.currentVolume, signalDetail.keyIndicators.signalVolume)"
                                    color="primary"
                                    class="ml-2"
                                ></v-progress-circular>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.marketSentiment') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getSentimentColor(signalDetail.keyIndicators.marketSentimentScore)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.marketSentimentScore, false) }}%
                                </v-chip>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.technical') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getCustomIndicatorsColor(signalDetail.keyIndicators.customIndicators.technical)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.customIndicators.technical, false) }}%
                                </v-chip>
                            </div>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.fundamental') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getCustomIndicatorsColor(signalDetail.keyIndicators.customIndicators.fundamental)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.customIndicators.fundamental, false) }}%
                                </v-chip>
                            </div>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.strength') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getCustomIndicatorsColor(signalDetail.keyIndicators.customIndicators.strength)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.customIndicators.strength, false) }}%
                                </v-chip>
                            </div>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.quality') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getCustomIndicatorsColor(signalDetail.keyIndicators.customIndicators.quality)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.customIndicators.quality, false) }}%
                                </v-chip>
                            </div>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.proximity') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getCustomIndicatorsColor(signalDetail.keyIndicators.customIndicators.proximity)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.customIndicators.proximity, false) }}%
                                </v-chip>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.rsi') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getRsiColor(signalDetail.keyIndicators.rsi)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.rsi, false) }}
                                </v-chip>
                            </div>
                            <div class="same-line-flex my-2">
                                <p class="ma-0 min-width-205"><strong>{{ $t('signalDetail.indicators.macd') }}:</strong>&nbsp;&nbsp;</p>
                                <v-chip
                                    link
                                    :color="getMacdColor(signalDetail.keyIndicators.macd)"
                                >
                                    {{ formatNumber(signalDetail.keyIndicators.macd, false) }}
                                </v-chip>
                            </div>
                            <v-divider class="my-2"></v-divider>
                            <p><strong>{{ $t('signalDetail.indicators.allTimeHigh') }}:</strong> $ {{ formatNumber(signalDetail.keyIndicators.historicMaxPrice, false) }}</p>
                            <p><strong>{{ $t('signalDetail.indicators.52wRange') }}:</strong> $ {{ formatNumber(signalDetail.keyIndicators.week52Low, false) }} - $ {{ formatNumber(signalDetail.keyIndicators.week52High, false) }}</p>
                            <p><strong>{{ $t('signalDetail.indicators.50dayMa') }}:</strong> $ {{ formatNumber(signalDetail.keyIndicators.day50MovingAverage, false) }}</p>
                            <p class="mb-0"><strong>{{ $t('signalDetail.indicators.200dayMa') }}:</strong> {{ signalDetail.keyIndicators.day2000MovingAverage ? '$ ' + formatNumber(signalDetail.keyIndicators.day2000MovingAverage, false) : '-' }}</p>
                        </div>
                    </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-col>
        
        <v-col lg=8 sm=12 md=12 cols=12 class="mx-0">
            <v-row>
                <!-- Block 3: Chart -->
                <v-col lg=12 sm=12 md=12 cols=12>
                    <TradingViewChart :prices="signalDetail.dailydataList" :stopLossPrice="signalDetail.keyIndicators.stopLossPrice" 
                            :breakoutPrice="signalDetail.keyIndicators.signalPrice" :targetPrice="signalDetail.keyIndicators.targetPrice">
                    </TradingViewChart>
                </v-col>

                <v-row class="earning-financial-row">
                    <!-- Block 4: Earnings Reports -->
                    <v-col lg=6 sm=12 md=6 cols=12 class="pb-0">
                        <v-card outlined elevation="2" class="card-style">
                        <v-card-title class="card-title"><p class="text-h5 text-truncate">{{ $t('signalDetail.earnings.title') }}</p></v-card-title>
                        <v-card-text class="px-0">
                            <v-skeleton-loader
                                v-if="loadingSignalDetail"
                                type="table-tbody"
                                max-height="297"
                            ></v-skeleton-loader>
                            <v-data-table 
                                v-else
                                class="material-table elevation-1"
                                mobile-breakpoint="0"
                                :show-expand="showTableRowButtonDetail"
                                :headers="earningsInfoHeaders" 
                                :items="signalDetail.earningsData" 
                                :hide-default-footer="true"
                                :height="300"
                                :expanded.sync="earningsInfoExpanded"
                                @click:row="toggleExpandTable(earningsInfoExpanded, $event)"
                                >
                                <template v-slot:item.reportedDate="{ item }">
                                    <span>{{ formatDate(item.reportedDate) }}</span>
                                </template>
                                <template v-slot:item.estimated="{ item }">
                                    <span>{{ item.estimatedEps ? formatNumber(item.estimatedEps, false) : '-' }}</span>
                                </template>
                                <template v-slot:item.reported="{ item }">
                                    <span v-if="!item.reportedEps">-</span>
                                    <div v-else class="text-center text-no-wrap">
                                        {{ formatNumber(item.reportedEps) }} 
                                        <v-icon v-if="item.estimatedEps && item.reportedEps > item.estimatedEps" color="success">mdi-arrow-top-right</v-icon>
                                        <v-icon v-if="item.estimatedEps && item.reportedEps < item.estimatedEps" color="error">mdi-arrow-bottom-right</v-icon>
                                    </div>
                                </template>
                                <template v-slot:expanded-item="{ headers, item }">
                                <td :colspan="headers.length">
                                    <div class="pt-3 pb-2">
                                    <v-row align="center" justify="center">
                                        <v-col cols="5" class="text-center pt-4">
                                            <span class="font-weight-medium">{{ $t('signalDetail.earnings.fiscalDateEnding') }}</span>
                                        </v-col>
                                        <v-col cols="7" class="text-center pt-4">
                                            <span class="font-weight-regular">{{ formatDate(item.fiscalDateEnding) }}</span>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="ma-2"></v-divider>
                                    <v-row align="center" justify="center">
                                        <v-col cols="5" class="text-center">
                                            <span class="font-weight-medium">{{ $t('signalDetail.earnings.surprise') }}</span>
                                        </v-col>
                                        <v-col cols="7" class="text-center">
                                            <span class="greyBold--text font-weight-regular">{{ item.surprice ? formatNumber(item.surprice) : '-' }}</span>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="ma-2"></v-divider>
                                    <v-row align="center" justify="center">
                                        <v-col cols="5" class="text-center">
                                            <span class="font-weight-medium">% {{ $t('signalDetail.earnings.surprise') }}</span>
                                        </v-col>
                                        <v-col cols="7" class="text-center">
                                            <div v-if="item.surprisePercentage">
                                                <v-chip link :color="item.surprisePercentage >= 0 ? 'success' :'secondary'">
                                                    {{formatNumber(item.surprisePercentage, true)}}%
                                                </v-chip>
                                            </div>
                                            <span v-else>-</span>
                                        </v-col>
                                    </v-row>
                                    </div>
                                </td>
                                </template>
                            </v-data-table>
                        </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Block 5: Financial Indicators -->
                    <v-col lg=6 sm=12 md=6 cols=12>
                        <v-card outlined elevation="2" class="card-style">
                        <v-card-title class="card-title"><p class="text-h5 text-truncate">{{ $t('signalDetail.financial.title') }}</p></v-card-title>
                        <v-card-text>
                            <v-skeleton-loader
                                v-if="loadingSignalDetail"
                                type="image"
                                class="height-400"
                            ></v-skeleton-loader>
                            <div v-else>
                                <p><strong>{{ $t('signalDetail.financial.peRatio') }}:</strong> 
                                {{ signalDetail.financialData.perRatio ? signalDetail.financialData.perRatio.replace(',','.') : '-' }}
                                </p>
                                <p><strong>{{ $t('signalDetail.financial.profitMargin') }}:</strong> 
                                    {{ signalDetail.financialData.profitMargin ? formatNumber(signalDetail.financialData.profitMargin, false) + '%' : '-' }}
                                </p>
                                <p><strong>{{ $t('signalDetail.financial.eps') }}:</strong> 
                                    {{ signalDetail.financialData.eps ? '$ ' + formatNumber(signalDetail.financialData.eps, false) : '-' }}
                                </p>
                                <p><strong>{{ $t('signalDetail.financial.roa') }}:</strong> 
                                    {{ signalDetail.financialData.returnOnAssetsTTM ? formatNumber(signalDetail.financialData.returnOnAssetsTTM, false) + '%' : '-' }}
                                </p>
                                <p><strong>{{ $t('signalDetail.financial.roe') }}:</strong> 
                                    {{ signalDetail.financialData.returnOnEquityTTM ? formatNumber(signalDetail.financialData.returnOnEquityTTM, false) + '%' : '-' }}
                                </p>
                                <div>
                                    <p class="mt-2 mb-1"><strong>{{ $t('signalDetail.financial.balanceSheet') }}:</strong></p>
                                    <ul>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.balanceSheet.fiscalDateEnding') }}</span>
                                            : {{ signalDetail.financialData.balanceSheet && signalDetail.financialData.balanceSheet.fiscalDateEnding ? 
                                                    formatDate(signalDetail.financialData.balanceSheet.fiscalDateEnding) : '-' }}
                                        </li>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.balanceSheet.totalShareholderEquity') }}</span>
                                            : {{ signalDetail.financialData.balanceSheet && signalDetail.financialData.balanceSheet.totalShareholderEquity ? 
                                                    '$ ' + formatNumber(signalDetail.financialData.balanceSheet.totalShareholderEquity, false) : '-' }}
                                        </li>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.balanceSheet.totalLiabilities') }}</span>
                                            : {{ signalDetail.financialData.balanceSheet && signalDetail.financialData.balanceSheet.totalLiabilities ? 
                                                    '$ ' + formatNumber(signalDetail.financialData.balanceSheet.totalLiabilities, false) : '-' }}
                                        </li>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.balanceSheet.debtToEquityRatio') }}</span>
                                            : {{ signalDetail.financialData.balanceSheet && signalDetail.financialData.balanceSheet.debtToEquityRatio ? 
                                                    '$ ' + formatNumber(signalDetail.financialData.balanceSheet.debtToEquityRatio, false) : '-' }}
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <p class="mt-2 mb-1"><strong>{{ $t('signalDetail.financial.incomeStatement') }}:</strong></p>
                                    <ul>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.incomeStatement.fiscalDateEnding') }}</span>
                                            : {{ signalDetail.financialData.incomeStatement && signalDetail.financialData.incomeStatement.fiscalDateEnding ? 
                                                    formatDate(signalDetail.financialData.incomeStatement.fiscalDateEnding) : '-' }}
                                        </li>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.incomeStatement.revenue') }}</span>
                                            : {{ signalDetail.financialData.incomeStatement && signalDetail.financialData.incomeStatement.totalRevenue ? '$ ' + 
                                                    formatNumber(signalDetail.financialData.incomeStatement.totalRevenue, false) : '-' }}
                                        </li>
                                        <li><span class="greyBold--text">{{ $t('signalDetail.financial.incomeStatement.netIncome') }}</span>
                                            : {{ signalDetail.financialData.incomeStatement && signalDetail.financialData.incomeStatement.netIncome ? '$ ' + 
                                                    formatNumber(signalDetail.financialData.incomeStatement.netIncome, false) : '-' }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-row>
        </v-col>
        </v-row>
        <BasicModal :modalCode="modalCode"></BasicModal>
    </v-container>
  </template>

<script> 
    import TradingViewChart from '@/components/Charts/TradingView/TradingViewChart';
    import BasicModal from '@/components/Modal/BasicModal';

    import { UtilsMixin } from '@/mixins/utils';
    import { mapState, mapActions } from 'vuex';

    import axios from 'axios';

    export default {
        name: 'SignalDetail',
        props: ['id'],
        components: {
            TradingViewChart, BasicModal
        },
        mixins: [ 
            UtilsMixin 
        ],
        data() {
            return {
                signalDetail: {
                    stock: null,
                    dailydataList: [],
                    earningsData: [],
                    financialData: {
                        balanceSheet: {},
                        incomeStatement: {}
                    },
                    keyIndicators: {
                        customIndicators: {}
                    },
                    triggered: false,
                    finished: false,
                    favorite: false
                },
                loadingSignalDetail: true,
                loadingFavoriteResult: false,
                modalCode: '',
                descriptionPreviewMaxSize: 130,
                showTableRowButtonDetail: false,
                earningsInfoHeaders: [
                    { text: this.$t('signalDetail.earnings.reportDate'), value: "reportedDate", sortable: false },
                    { text: this.$t('signalDetail.earnings.estimated'), value: "estimated", align: 'center', sortable: false },
                    { text: this.$t('signalDetail.earnings.reported'), value: "reported", align: 'center', sortable: false }
                ],
                earningsInfoExpanded: [],
            };
        },
        created() {
            // Si no se encuentra el ID de la señal se lanza error
            if (!this.id) {
                this.showErrorModal();
            }

            // Controlar que al regresar de la pantalla completa se resetee la descripción de la acción
            document.addEventListener('fullscreenchange', () => {
                this.updateDescriptionStatus(true);
            });
        },
        mounted() {
            this.checkDetailButtonInTableColumn(this.$vuetify.breakpoint.name);

            // API calls
            this.getSignalDetail();
        },
        watch: {
            '$vuetify.breakpoint.name'(newVal) {
                this.checkDetailButtonInTableColumn(newVal);
            },
            'signalDetail.stock'() {
                this.getHistoricalDailyData();
            }
        },
        computed: {
            ...mapState('modal', ['modals']),
            isFavoriteSignal () {
                return this.signalDetail.favorite;
            }
        },
        methods: {
            // ---- Datos cargados del store ----
            ...mapActions('modal', ['showModal', 'hideModal']),
            // ---- FUNCIONES UTILIZADAS POR LOS COMPONENTES EN PANTALLA  ----
            // Mostrar u ocultar botón de detalle en las tablas cuando es una pantalla mediana o mayor
            checkDetailButtonInTableColumn(size) {
                if (size === 'sm' || size === 'md' || size === 'lg' || size === 'xl') {
                if (!this.earningsInfoHeaders.some(header => header.value === 'data-table-expand')) {
                    this.earningsInfoHeaders.push({ text: '', value: 'data-table-expand' });
                    this.showTableRowButtonDetail = true;
                }
                } else {
                    this.earningsInfoHeaders = this.earningsInfoHeaders.filter(header => header.value !== 'data-table-expand');
                    this.showTableRowButtonDetail = false;
                }
            },
            // Permite mostrar u ocultar la descripción completa de una acción
            updateDescriptionStatus(reset) {
                let dots = document.getElementById("desc-dots");
                let more = document.getElementById("desc-more");
                let link = document.getElementById("desc-link");

                if (reset || dots.style.display === "none") {
                    dots.style.display = "inline";
                    more.style.display = "none";
                    link.innerHTML = this.$t('signalDetail.overview.showMore');
                } else {
                    dots.style.display = "none";
                    more.style.display = "inline";
                    link.innerHTML = this.$t('signalDetail.overview.showLess');
                }
            },
            showErrorModal() {
                this.modalCode = 'apiErrorModal';
                this.showDefaultErrorModal(this.modalCode);
            },
            // ---- PETICIONES AL BACKEND ----
            // Consultar los datos resumen de señales
            async getSignalDetail() {
                this.loadingSignalDetail = true;
                try {
                    const response = await axios.get('/signal/getSignalDetail', { params: { signalId: this.id } });
                    this.signalDetail = response.data;
                } catch (e) {
                    this.showErrorModal();
                } finally {
                    this.loadingSignalDetail = false;
                }
            },
            // Consultar los datos diarios históricos de la acción
            // (Se realiza en segundo plano una vez cargados los datos de la señal)
            async getHistoricalDailyData() {
                try {
                    const response = await axios.get('/signal/getPreviousHistoricalDailyData', { params: { 
                        idStock: this.signalDetail.stock.id, 
                        idDailyDataLimit: this.signalDetail.dailydataList[0].id
                    }});
                    if (response.data && response.data.length > 0) {
                        this.signalDetail.dailydataList.unshift(...response.data);
                    }
                } catch (e) {
                    this.showErrorModal();
                }
            },
            // Actualizar señal favorita del usuario
            async updateFavoriteStatus() {
                this.loadingFavoriteResult = true;
                try {
                    const response = await axios.get('/signal/updateSignalFavoriteStatus', { params: { 
                        signalId: this.id
                    }});
                    this.signalDetail.favorite = response.data;
                    this.loadingFavoriteResult = false;
                } catch (e) {
                    this.showErrorModal();
                    this.loadingFavoriteResult = false;
                }
            },
        },
    };
</script>

<style scoped>
    .earning-financial-row {
        width: 100% !important;
    }
</style>